/**
 * Página de Tarefas - Kanban Board com Drag and Drop
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  GroupOutlined
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import MainLayout from '../components/Layout/MainLayout';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAuth } from '../contexts/AuthContext';
import { 
  LeadGroup, 
  fetchGroups,
  deleteGroupAsync, 
  updateGroupStatusAsync 
} from '../store/slices/groupsSlice';
import LeadListModal from '../components/Groups/LeadListModal';
import KanbanColumn from '../components/Kanban/KanbanColumn';
import KanbanCard from '../components/Kanban/KanbanCard';

const TasksPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isAdmin } = useAuth();
  const { groups, loading } = useAppSelector((state) => state.groups);

  // Carregar grupos do backend ao montar componente
  useEffect(() => {
    if (user) {
      dispatch(fetchGroups({ userId: user.uid, isAdmin }));
    }
  }, [dispatch, user, isAdmin]);
  
  const [selectedGroup, setSelectedGroup] = useState<LeadGroup | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; groupId: string } | null>(null);
  const [activeGroup, setActiveGroup] = useState<LeadGroup | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleCardClick = async (group: LeadGroup) => {
    // Verificar permissão: admin pode ver tudo, usuário normal só seus próprios grupos
    if (isAdmin || group.userId === user?.uid) {
      try {
        // Buscar leads do grupo
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/groups/${group.id}/leads`
        );
        
        if (response.ok) {
          const leads = await response.json();
          // Atualizar o grupo com os leads
          setSelectedGroup({ ...group, leads });
        } else {
          console.error('Erro ao carregar leads do grupo');
          setSelectedGroup(group);
        }
      } catch (error) {
        console.error('Erro ao buscar leads:', error);
        setSelectedGroup(group);
      }
    } else {
      // Opcional: mostrar mensagem de que não tem permissão
      console.log('Sem permissão para visualizar este grupo');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, groupId: string) => {
    event.stopPropagation();
    setMenuAnchor({ element: event.currentTarget, groupId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDeleteGroup = () => {
    if (menuAnchor) {
      dispatch(deleteGroupAsync(menuAnchor.groupId));
      handleMenuClose();
    }
  };

  const handleDragStart = (event: any) => {
    const group = groups.find((g) => g.id === event.active.id);
    setActiveGroup(group || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveGroup(null);
    const { active, over } = event;

    if (!over) return;

    const groupId = active.id as string;
    const newStatus = over.id as LeadGroup['status'];

    dispatch(updateGroupStatusAsync({ groupId, status: newStatus }));
  };

  const groupsByStatus = {
    novo: groups.filter((g) => g.status === 'novo'),
    trabalhando: groups.filter((g) => g.status === 'trabalhando'),
    finalizado: groups.filter((g) => g.status === 'finalizado'),
  };

  const getColumnStats = (status: LeadGroup['status']) => {
    const groupsInColumn = groupsByStatus[status];
    return {
      totalGroups: groupsInColumn.length,
      totalLeads: groupsInColumn.reduce((sum, g) => sum + g.stats.total, 0),
      contactado: groupsInColumn.reduce((sum, g) => sum + g.stats.contactado, 0),
      recusou: groupsInColumn.reduce((sum, g) => sum + g.stats.recusou, 0),
      semContato: groupsInColumn.reduce((sum, g) => sum + g.stats.semContato, 0),
      emContato: groupsInColumn.reduce((sum, g) => sum + g.stats.emContato, 0),
      fechado: groupsInColumn.reduce((sum, g) => sum + g.stats.fechado, 0),
    };
  };

  return (
    <MainLayout>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ mb: 2, px: 1 }}>
          <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
            Tarefas - Kanban
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Gerencie seus grupos de leads por status
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: '#FF6B35' }} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && groups.length === 0 && (
          <Card
            sx={{
              p: 8,
              mx: 1,
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.1)',
              textAlign: 'center',
            }}
          >
            <GroupOutlined sx={{ fontSize: 80, color: 'rgba(255, 107, 53, 0.3)', mb: 2 }} />
            <Typography variant="h5" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Nenhum grupo importado
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Importe leads da busca para começar a gerenciar suas tarefas
            </Typography>
          </Card>
        )}

        {/* Kanban Board - Largura Total */}
        {!loading && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              flex: 1,
              overflow: 'hidden',
              px: 1,
            }}
          >
            <KanbanColumn
              id="novo"
              title="Novo"
              color="#757575"
              stats={getColumnStats('novo')}
            >
              {groupsByStatus.novo.map((group) => (
                <KanbanCard
                  key={group.id}
                  group={group}
                  onCardClick={handleCardClick}
                  onMenuOpen={handleMenuOpen}
                  isAdmin={isAdmin}
                  currentUserId={user?.uid}
                />
              ))}
            </KanbanColumn>

            <KanbanColumn
              id="trabalhando"
              title="Trabalhando"
              color="#FF6B35"
              stats={getColumnStats('trabalhando')}
            >
              {groupsByStatus.trabalhando.map((group) => (
                <KanbanCard
                  key={group.id}
                  group={group}
                  onCardClick={handleCardClick}
                  onMenuOpen={handleMenuOpen}
                  isAdmin={isAdmin}
                  currentUserId={user?.uid}
                />
              ))}
            </KanbanColumn>

            <KanbanColumn
              id="finalizado"
              title="Finalizado"
              color="#4caf50"
              stats={getColumnStats('finalizado')}
            >
              {groupsByStatus.finalizado.map((group) => (
                <KanbanCard
                  key={group.id}
                  group={group}
                  onCardClick={handleCardClick}
                  onMenuOpen={handleMenuOpen}
                  isAdmin={isAdmin}
                  currentUserId={user?.uid}
                />
              ))}
            </KanbanColumn>
          </Box>

          <DragOverlay>
            {activeGroup ? (
              <KanbanCard
                group={activeGroup}
                onCardClick={() => {}}
                onMenuOpen={() => {}}
                isDragging
              />
            ) : null}
          </DragOverlay>
        </DndContext>
        )}

        {/* Menu de Ações */}
        <Menu
          anchorEl={menuAnchor?.element}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: '#1a1a1a',
              border: '1px solid rgba(255, 107, 53, 0.2)',
            }
          }}
        >
          <MenuItem
            onClick={handleDeleteGroup}
            sx={{
              color: '#f44336',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
              }
            }}
          >
            Excluir Grupo
          </MenuItem>
        </Menu>

        {/* Modal de Lista de Leads */}
        {selectedGroup && (
          <LeadListModal
            open={Boolean(selectedGroup)}
            onClose={() => setSelectedGroup(null)}
            group={selectedGroup}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default TasksPage;
